// WP-UX-1 — the §10.1.1 colour guard (UX v1.6; BRS §9.1 branding, D-F5).
// Tenant colours enter the apps ONLY through this one pure function; the
// office app, the field app and (when its renderer lands, D-WPUX-1-1) the
// worker all call it. "A guard on one surface is no guard."
//
// For a tenant primary (→ `identity`) and accent (→ `brand.*`) it:
//   1. SPLITS the accent into `fill` (as given — large/decorative use only)
//      and `accessible` (same OKLCH hue + chroma, lightness lowered until it
//      reaches >= 4.5:1 against BOTH `surface` and `surface.sunken`; chroma is
//      reduced only when the point leaves the sRGB gamut);
//   2. FLIPS the on-colours: text on `fill` and on `identity` is white if white
//      passes 4.5:1, otherwise `ink`;
//   3. FALLS BACK to the Steier default for a role that still cannot pass;
//   4. REPORTS `adjusted: true` (and why) so O-15's preview can say "Adjusted
//      for legibility: your colour is used for fills; a darker shade is used
//      for text and buttons."
// Status roles never pass through here — they are not inputs.
//
// Dependency-free and DOM/RN-free on purpose: it runs in Vite, Metro/Hermes
// and node:test. The sRGB <-> OKLCH conversion is Björn Ottosson's OKLab
// (https://bottosson.github.io/posts/oklab/), written out here rather than
// pulled from a colour library (the contract surface must stay pure).
import { COLOUR_ROLES } from "./design-tokens.js";
import { isHexColour, STEIER_DEFAULT_BRANDING } from "./branding.js";
/** WCAG 2.x minimum for normal-size text (§10.7). */
export const TEXT_CONTRAST_MIN = 4.5;
/** The two canvases every `accessible` shade must pass on (§10.1.1 step 1). */
export const GUARD_SURFACES = [COLOUR_ROLES.surface, COLOUR_ROLES["surface.sunken"]];
// ---------------------------------------------------------------------------
// Hex / WCAG helpers
// ---------------------------------------------------------------------------
/** `#abc` / `#AbCdEf` → `#AABBCC`. Throws on a non-hex input (validate first). */
export function normaliseHex(hex) {
    if (!isHexColour(hex))
        throw new RangeError(`not a #rgb/#rrggbb colour: ${String(hex)}`);
    const h = hex.slice(1);
    const full = h.length === 3 ? h.split("").map((c) => c + c).join("") : h;
    return `#${full.toUpperCase()}`;
}
function hexToRgb(hex) {
    const full = normaliseHex(hex).slice(1);
    return [0, 2, 4].map((i) => parseInt(full.slice(i, i + 2), 16) / 255);
}
function rgbToHex(rgb) {
    return `#${rgb
        .map((c) => Math.round(clamp01(c) * 255).toString(16).padStart(2, "0"))
        .join("")
        .toUpperCase()}`;
}
function clamp01(v) {
    return v < 0 ? 0 : v > 1 ? 1 : v;
}
function srgbToLinear(c) {
    return c <= 0.04045 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
}
function linearToSrgb(c) {
    const v = clamp01(c);
    return v <= 0.0031308 ? 12.92 * v : 1.055 * v ** (1 / 2.4) - 0.055;
}
/** WCAG 2.x relative luminance of a hex colour. */
export function relativeLuminance(hex) {
    const [r, g, b] = hexToRgb(hex).map(srgbToLinear);
    return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}
/** WCAG 2.x contrast ratio (1–21), order-independent. */
export function contrastRatio(a, b) {
    const la = relativeLuminance(a);
    const lb = relativeLuminance(b);
    const [hi, lo] = la > lb ? [la, lb] : [lb, la];
    return (hi + 0.05) / (lo + 0.05);
}
/** sRGB hex → OKLCH. */
export function hexToOklch(hex) {
    const [r, g, b] = hexToRgb(hex).map(srgbToLinear);
    const l_ = Math.cbrt(0.4122214708 * r + 0.5363325363 * g + 0.0514459929 * b);
    const m_ = Math.cbrt(0.2119034982 * r + 0.6806995451 * g + 0.1073969566 * b);
    const s_ = Math.cbrt(0.0883024619 * r + 0.2817188376 * g + 0.6299787005 * b);
    const L = 0.2104542553 * l_ + 0.793617785 * m_ - 0.0040720468 * s_;
    const A = 1.9779984951 * l_ - 2.428592205 * m_ + 0.4505937099 * s_;
    const B = 0.0259040371 * l_ + 0.7827717662 * m_ - 0.808675766 * s_;
    const c = Math.hypot(A, B);
    const h = ((Math.atan2(B, A) * 180) / Math.PI + 360) % 360;
    return { l: L, c, h };
}
function oklchToLinearRgb({ l, c, h }) {
    const rad = (h * Math.PI) / 180;
    const A = c * Math.cos(rad);
    const B = c * Math.sin(rad);
    const l_ = l + 0.3963377774 * A + 0.2158037573 * B;
    const m_ = l - 0.1055613458 * A - 0.0638541728 * B;
    const s_ = l - 0.0894841775 * A - 1.291485548 * B;
    const L = l_ ** 3;
    const M = m_ ** 3;
    const S = s_ ** 3;
    return [
        4.0767416621 * L - 3.3077115913 * M + 0.2309699292 * S,
        -1.2684380046 * L + 2.6097574011 * M - 0.3413193965 * S,
        -0.0041960863 * L - 0.7034186147 * M + 1.707614701 * S,
    ];
}
const GAMUT_EPSILON = 1e-6;
function inGamut(rgb) {
    return rgb.every((v) => v >= -GAMUT_EPSILON && v <= 1 + GAMUT_EPSILON);
}
/**
 * OKLCH → sRGB hex, gamut-mapped by reducing CHROMA only (lightness and hue
 * are kept — §10.1.1 "chroma reduced only if the hue leaves gamut"). Lightness
 * is clamped to 0–1 first so an achromatic point is always representable.
 */
export function oklchToHex(colour) {
    const l = clamp01(colour.l);
    let c = Math.max(0, colour.c);
    let rgb = oklchToLinearRgb({ l, c, h: colour.h });
    // Chroma shrinks geometrically until the point is inside the cube; at c = 0
    // the colour is the neutral of lightness l, which is always in gamut.
    while (!inGamut(rgb) && c > 1e-4) {
        c *= 0.9;
        rgb = oklchToLinearRgb({ l, c, h: colour.h });
    }
    if (!inGamut(rgb))
        rgb = oklchToLinearRgb({ l, c: 0, h: colour.h });
    return rgbToHex(rgb.map(linearToSrgb));
}
// ---------------------------------------------------------------------------
// The guard
// ---------------------------------------------------------------------------
const WHITE = COLOUR_ROLES["on.identity"];
const INK = COLOUR_ROLES.ink;
/** The Steier default accent (= §10.1 `brand.accessible`), normalised for comparison. */
const STEIER_DEFAULT_ACCENT = normaliseHex(STEIER_DEFAULT_BRANDING.accentColor);
function passesAll(hex, surfaces) {
    return surfaces.every((s) => contrastRatio(hex, s) >= TEXT_CONTRAST_MIN);
}
/**
 * §10.1.1 step 1 — the accessible shade of a colour: lightness lowered in
 * OKLCH (hue and chroma kept; chroma reduced only for gamut) until the colour
 * reaches ≥ 4.5:1 against every surface. Returns `null` when no lightness
 * can (only possible against a dark surface set — never §10.1's).
 */
export function accessibleShadeOf(hex, surfaces = GUARD_SURFACES) {
    const start = normaliseHex(hex);
    if (passesAll(start, surfaces))
        return start;
    const { l: l0, c, h } = hexToOklch(start);
    const ok = (l) => passesAll(oklchToHex({ l, c, h }), surfaces);
    if (!ok(0))
        return null;
    // Bisection for the highest lightness that passes (contrast is monotonic in
    // lightness up to hex rounding), then nudge down past any rounding wobble.
    let lo = 0;
    let hi = l0;
    for (let i = 0; i < 40; i += 1) {
        const mid = (lo + hi) / 2;
        if (ok(mid))
            lo = mid;
        else
            hi = mid;
    }
    let l = lo;
    while (!ok(l))
        l -= 0.0005;
    return oklchToHex({ l, c, h });
}
/** A darker/duller step of an accessible shade (hover / pressed), kept passing. */
function darkerStep(hex, dL, cScale, surfaces) {
    const { l, c, h } = hexToOklch(hex);
    let step = oklchToHex({ l: l - dL, c: c * cScale, h });
    let guardL = l - dL;
    while (!passesAll(step, surfaces) && guardL > 0) {
        guardL -= 0.005;
        step = oklchToHex({ l: guardL, c: c * cScale, h });
    }
    return step;
}
/** §10.1.1 step 2 — white if it passes 4.5, else ink if it passes, else the better of the two. */
function onColourFor(bg) {
    const vsWhite = contrastRatio(WHITE, bg);
    if (vsWhite >= TEXT_CONTRAST_MIN)
        return { colour: WHITE, passes: true };
    const vsInk = contrastRatio(INK, bg);
    if (vsInk >= TEXT_CONTRAST_MIN)
        return { colour: INK, passes: true };
    return { colour: vsWhite >= vsInk ? WHITE : INK, passes: false };
}
/**
 * A light tint of the accent's hue for selected rows / active nav. Its WCAG
 * luminance is raised until it is at least `surface.sunken`'s, so any shade
 * that passes 4.5 on sunken passes on it too (OKLCH lightness is not WCAG
 * luminance — at a fixed L, blue/purple hues sit darker than yellow ones).
 */
function brandSurfaceFor(accent) {
    const { c, h } = hexToOklch(accent);
    const floor = relativeLuminance(COLOUR_ROLES["surface.sunken"]);
    const chroma = Math.min(c, 0.02);
    let l = 0.97;
    let tint = oklchToHex({ l, c: chroma, h });
    while (relativeLuminance(tint) < floor && l < 1) {
        l += 0.0025;
        tint = oklchToHex({ l, c: chroma, h });
    }
    return tint;
}
/**
 * The §10.1.1 colour guard. Invalid or missing inputs resolve to the Steier
 * defaults and are NOT reported as adjustments (the tenant typed nothing).
 */
export function guardTenantColours(input = {}, options = {}) {
    const surfaces = options.surfaces ?? GUARD_SURFACES;
    const reasons = [];
    // identity (tenant primary)
    const primary = isHexColour(input.primary) ? normaliseHex(input.primary) : COLOUR_ROLES.identity;
    let identity = primary;
    let onIdentity = onColourFor(identity);
    if (!onIdentity.passes) {
        identity = COLOUR_ROLES.identity;
        onIdentity = onColourFor(identity);
        reasons.push("IDENTITY_FALLBACK");
    }
    if (onIdentity.colour !== WHITE)
        reasons.push("ON_IDENTITY_DARK");
    // brand.* (tenant accent)
    const accent = isHexColour(input.accent) ? normaliseHex(input.accent) : STEIER_DEFAULT_ACCENT;
    // UX-4a (UX2-SHELL-6, owner-approved 2026-10-19): the Steier default accent
    // IS the §10.1 `brand.accessible` token, whose paired decorative role is the
    // lighter §10.1 `brand.fill` — so a tenant that saves (or keeps) the default
    // gets the §10.1 pair, never `fill` collapsed onto `accessible`. The default
    // fill carries white at large-text grade (§10.1.1 step 2 build note: 4.04),
    // so it is not an ON_FILL_DARK adjustment. Any other accent: fill as given.
    const isSteierDefaultAccent = accent === STEIER_DEFAULT_ACCENT;
    const fill = isSteierDefaultAccent ? normaliseHex(COLOUR_ROLES["brand.fill"]) : accent;
    const onFill = isSteierDefaultAccent ? { colour: WHITE } : onColourFor(fill);
    if (onFill.colour !== WHITE)
        reasons.push("ON_FILL_DARK");
    let accessible = accessibleShadeOf(accent, surfaces);
    if (accessible === null) {
        accessible = COLOUR_ROLES["brand.accessible"];
        reasons.push("ACCESSIBLE_FALLBACK");
    }
    else if (accessible !== accent) {
        reasons.push("ACCESSIBLE_DARKENED");
    }
    // UX-4c (UX2-SHELL-9, owner-approved 2026-10-20 — the rest of SHELL-6): the
    // default accent gets the WHOLE §10.1 brand table, not hover / pressed /
    // surface derived from it — the derivation lands a hair off the tokens
    // (#174FB1 / #134190 / #F0F5FE vs #174FB0 / #12408F / #E8F0FD), so a tenant
    // that saved the default painted a different palette from one that never
    // branded. Every other accent: derived exactly as before.
    const accessibleHover = isSteierDefaultAccent
        ? normaliseHex(COLOUR_ROLES["brand.accessible.hover"])
        : darkerStep(accessible, 0.06, 0.88, surfaces);
    const accessiblePressed = isSteierDefaultAccent
        ? normaliseHex(COLOUR_ROLES["brand.accessible.pressed"])
        : darkerStep(accessible, 0.12, 0.75, surfaces);
    const brandSurface = isSteierDefaultAccent ? normaliseHex(COLOUR_ROLES["brand.surface"]) : brandSurfaceFor(fill);
    return {
        identity,
        onIdentity: onIdentity.colour,
        fill,
        onFill: onFill.colour,
        accessible,
        accessibleHover,
        accessiblePressed,
        brandSurface,
        adjusted: reasons.length > 0,
        reasons,
    };
}
//# sourceMappingURL=colour-guard.js.map